import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { AlertPanel } from '../AlertPanel';
import { AdvertProvider } from 'shared/components/Adverts/AdvertProviderDfp';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
import { SeoLinksComponent } from '../SeoLinks';
import {
    DFP_AD_UNIT,
    DfpTargeting,
    AD_UNIT_ROOTS
} from '../../shared/components/Adverts/types';
import { SeoLinks } from '../SeoLinks/SeoLinks.types';
import { AlertCtaModal } from '../AlertCtaModal';
import { breakpoints } from '../common/constants';

import { SearchResultsWrapper, SideBar, InnerWrapper } from './Layout.styles';
import { Links } from '../SearchContext';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { getPageSuffixForAdUnit } from 'shared/components/Adverts/lib';
import {
    PAGE_MAPPING,
    TEXT_LINK_PAGE_MAPPING
} from 'shared/components/Adverts/constants';
import SearchAndFitersWrapper from 'search/SearchAndFiltersWrapper/SearchAndFiltersWrapper';

const Sticky = styled.div`
    position: sticky;
    top: 0;
    width: 100%;
`;

const Main = styled.main`
    width: 100%;
`;

type SearchResultsLayout = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    numberOfRegularListings: number;
    pageNumber?: number;
    links: Links;
};

const SearchResultsLayout: FC<SearchResultsLayout> = ({
    dfpAdTargeting,
    seoAccordions,
    children,
    pageNumber,
    links
}) => {
    const isLoggedIn = useIsLoggedIn();

    const [adProviderVal, updateAdProviderVal] = useState({
        showPlaceHolderContent: false,
        reportMode: false,
        targeting: {
            ...dfpAdTargeting,
            signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
        },
        pageNumber
    });

    useEffect(() => {
        updateAdProviderVal({
            showPlaceHolderContent: false,
            reportMode: false,
            targeting: {
                ...dfpAdTargeting,
                signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
            },
            pageNumber
        });
    }, [dfpAdTargeting, isLoggedIn, pageNumber]);

    const adUnitPage = getPageSuffixForAdUnit(PAGE_MAPPING);

    return (
        <>
            <AdvertProvider value={adProviderVal}>
                <SearchAndFitersWrapper>
                    <SearchResultsWrapper suppressHydrationWarning={true}>
                        <InnerWrapper>
                            <Main data-testid="search-content">{children}</Main>
                            <SideBar data-testid="search-sidebar">
                                <AlertPanel links={links} />
                                <AdvertSlotDfp
                                    position={0}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[[300, 16]]}
                                    adUnitCode={[
                                        AD_UNIT_ROOTS.TEXT_LINK,
                                        getPageSuffixForAdUnit(
                                            TEXT_LINK_PAGE_MAPPING
                                        )
                                    ]}
                                />
                                <AdvertSlotDfp
                                    position={0}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[[300, 250]]}
                                    adUnitCode={[AD_UNIT_ROOTS.MPU, adUnitPage]}
                                />
                                <AdvertSlotDfp
                                    position={0}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[
                                        [300, 160],
                                        [300, 50],
                                        [300, 100]
                                    ]}
                                    adUnitCode={[
                                        AD_UNIT_ROOTS.PARTNERSHIP,
                                        adUnitPage
                                    ]}
                                />
                                <AdvertSlotDfp
                                    position={0}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[[300, 120], 'fluid']}
                                    adUnitCode={DFP_AD_UNIT.VALUATION_BOOSTER}
                                />
                                <AdvertSlotDfp
                                    position={1}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[
                                        [300, 160],
                                        [300, 50],
                                        [300, 100]
                                    ]}
                                    adUnitCode={[
                                        AD_UNIT_ROOTS.PARTNERSHIP,
                                        adUnitPage
                                    ]}
                                />

                                {!!seoAccordions.length && (
                                    <SeoLinksComponent />
                                )}

                                <Sticky>
                                    <AdvertSlotDfp
                                        position={0}
                                        showOnly={[
                                            '>',
                                            breakpoints.lsrpSidebar
                                        ]}
                                        sizes={[
                                            [120, 600],
                                            [160, 600],
                                            [300, 600]
                                        ]}
                                        adUnitCode={[
                                            AD_UNIT_ROOTS.SKY_SCRAPER,
                                            adUnitPage
                                        ]}
                                    />
                                </Sticky>
                            </SideBar>
                        </InnerWrapper>
                    </SearchResultsWrapper>
                    <AlertCtaModal links={links} />
                </SearchAndFitersWrapper>
            </AdvertProvider>
        </>
    );
};

export default SearchResultsLayout;
